import { useState, useEffect } from "react";
import { Button, Form, Input, Typography, notification, Space } from "antd";
import { useBoundStore } from "store";
import { updateTenantSettings, testAnthropicKey } from "tenants/services/TenantService";
import { IEligibilitySettings } from "tenants/models";

export const EligibilityConfig = () => {
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  const { tenant } = useBoundStore();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (tenant?.settings?.eligibility) {
      form.setFieldsValue(tenant.settings.eligibility);
    }
  }, [tenant, form]);

  const onSave = async (values: IEligibilitySettings) => {
    if (!tenant) return;
    setSaving(true);
    try {
      await updateTenantSettings(tenant.id, { eligibility: values });
      api.success({
        message: "Settings Saved",
        description: "Eligibility checker LLM extraction key updated.",
      });
    } catch (error: any) {
      api.error({
        message: "Error",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Failed to save settings",
      });
    } finally {
      setSaving(false);
    }
  };

  const onTestConnection = async () => {
    if (!tenant) return;
    const values = form.getFieldsValue();
    if (!values.anthropicApiKey) {
      api.warning({
        message: "Missing Key",
        description: "Enter an Anthropic API key before testing.",
      });
      return;
    }
    setTesting(true);
    try {
      const result = await testAnthropicKey(tenant.id, values.anthropicApiKey);
      if (result.success) {
        api.success({ message: "Connection Successful", description: result.message });
      } else {
        api.error({ message: "Connection Failed", description: result.message });
      }
    } catch (error: any) {
      api.error({
        message: "Connection Failed",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Failed to test connection",
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div>
      {contextHolder}
      <Typography.Title level={4}>
        Eligibility Checker — LLM Extraction (Optional)
      </Typography.Title>
      <Typography.Paragraph type='secondary'>
        Optional: configure an Anthropic API key to extract bill data using Claude
        instead of the local parser. When set, ALL eligibility-checker bill uploads
        for this tenant are sent to Anthropic for extraction. Leave blank to keep
        using fully local extraction (no third-party egress).
      </Typography.Paragraph>
      <Form form={form} layout='vertical' onFinish={onSave} requiredMark={false}>
        <Form.Item name='anthropicApiKey' label='Anthropic API Key'>
          <Input.Password placeholder='sk-ant-...' />
        </Form.Item>
        <Space>
          <Button type='primary' htmlType='submit' loading={saving}>
            Save Settings
          </Button>
          <Button onClick={onTestConnection} loading={testing}>
            Test Connection
          </Button>
        </Space>
      </Form>
    </div>
  );
};
